import * as React from 'react'

import { makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { ResourceTypeId } from '../../constants/resourceTypes'
import Level from '../../models/LocationObjects/Level'
import { renderResourceRecordIcon } from '../../resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '../../resources/icons/resourceSemanticIcons'
import BuildingsStore from '../../stores/domain/Buildings.store'
import LevelsStore from '../../stores/domain/Levels.store'
import VerticalObjectsStore from '../../stores/domain/VerticalObjects.store'
import { ListSelector } from '../ListSelector/ListSelector'
import LocationResourcePicker from '../LocationPicker/LocationResourcePicker'
import { IResourceChipItem } from '../ResourceChipList'
import ResponsiveSurface from '../ResponsiveSurface/ResponsiveSurface'
import StruxhubResourceSelector from '../StruxhubInputs/StruxhubSelector/StruxhubResourceSelector'

const selectStairLevels = 'Select stair levels'

// As the workflow form's Location field shows its chips.
const ICON_CLASS_NAME =
  'tw:block tw:size-4 tw:text-muted-foreground tw:ml-0.5 tw:mr-1'

const toChipItem = (level: Level): IResourceChipItem => ({
  id: level.id,
  label: level.name,
  isDeleted: level.isDeleted,
  color: level.color,
  iconName: level.iconName,
})

const renderLevelIcon = (item: IResourceChipItem) =>
  renderResourceRecordIcon(
    ResourceTypeId.LOCATIONS,
    item,
    renderResourceSemanticIcon('location', ICON_CLASS_NAME),
    ICON_CLASS_NAME,
  )

interface IProps {
  verticalObjectId: string
  selectedLevelIds: string[]
  isMandatory?: boolean
  label?: string

  levelsStore?: LevelsStore
  buildingsStore?: BuildingsStore
  verticalObjectsStore?: VerticalObjectsStore
  isMobileEnv?: boolean
  onChange(levels: string[]): void
}

@inject('buildingsStore', 'levelsStore', 'verticalObjectsStore', 'isMobileEnv')
@observer
export default class VerticalObjectLevelsSelector extends React.Component<IProps> {
  public static defaultProps = {
    selectedLevelIds: [],
  }
  @observable private isLevelSelectorDisplayed: boolean = false
  private readonly anchorRef = React.createRef<HTMLDivElement>()

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    if (!this.verticalObject || !this.verticalObject.accessibleLevels.length) {
      return null
    }

    const {
      label = selectStairLevels,
      isMandatory,
      selectedLevelIds,
      isMobileEnv,
    } = this.props

    return (
      <div ref={this.anchorRef}>
        <StruxhubResourceSelector
          label={label}
          isRequired={isMandatory}
          isHelperTextCondensed={true}
          items={this.selectedLevels.map(toChipItem)}
          onClick={this.toggleLevelsSelector}
          onRemove={this.removeLevel}
          renderIcon={renderLevelIcon}
        />
        <ResponsiveSurface
          open={this.isLevelSelectorDisplayed}
          onOpenChange={open => !open && this.hideLevelSelector()}
          title={label}
          isMobileMode={isMobileEnv}
          anchor={this.anchorRef.current}
          sizing="fill"
        >
          <ListSelector.Title title={label} onClose={this.hideLevelSelector} />
          <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
            <LocationResourcePicker
              mode="multi"
              allowEmptySelection={true}
              selectedIds={selectedLevelIds.filter(id =>
                this.allowedLevelIds.includes(id),
              )}
              allowedIds={this.allowedLevelIds}
              onSelect={this.onApplyClosedLevels}
              onClose={this.hideLevelSelector}
            />
          </div>
        </ResponsiveSurface>
      </div>
    )
  }

  private toggleLevelsSelector = () => {
    this.isLevelSelectorDisplayed = !this.isLevelSelectorDisplayed
  }

  private hideLevelSelector = () => {
    this.isLevelSelectorDisplayed = false
  }

  private removeLevel = (id: string) => {
    this.props.onChange(this.props.selectedLevelIds.filter(l => l !== id))
  }

  private onApplyClosedLevels = (levelIds: string[]) => {
    this.isLevelSelectorDisplayed = false
    this.props.onChange(levelIds)
  }
  // The building's levels the object reaches, as the legacy selector offered them.
  private get allowedLevelIds(): string[] {
    const { buildingsStore, levelsStore } = this.props
    const { accessibleLevels, parent } = this.verticalObject
    const building = parent && buildingsStore.byId.get(parent.parentId)
    return levelsStore.list
      .filter(l => l.isParent(building) && accessibleLevels.includes(l.id))
      .map(l => l.id)
  }

  private get verticalObject() {
    const { verticalObjectsStore, verticalObjectId } = this.props
    return verticalObjectsStore.byId.get(verticalObjectId)
  }

  private get selectedLevels() {
    return this.props.levelsStore.list.filter(l =>
      this.props.selectedLevelIds.includes(l.id),
    )
  }
}
